import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import { styled } from '@mui/material/styles';
import {
  Collapse,
  Divider,
  Drawer,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Menu,
  MenuItem
} from '@mui/material';
import { NavLink } from 'react-router-dom';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { signOut } from '../../redux/slices/authSlice';
import useRoutes, { Route } from '../../containers/Router/useRoutes';
import { selectCities } from '../../redux/slices/citiesSlice';
import { selectAdmins, setCurrentCity } from '../../redux/slices/adminsSlice';
import Logo from '../Logo';
import { RootState } from '../../redux/store';
import usePermissions from '../../hooks/usePermissions';
import { useThemeMode } from '../../contexts/ThemeContext';
import { useResponsiveLayout } from '../../contexts/ResponsiveLayoutContext';
import useIsHotelOnlyAdmin from '../../hooks/useIsHotelOnlyAdmin';
import { changeAppLanguage } from '../../i18n';

const PREFIX = 'Sidebar';

const classes = {
  drawer: `${PREFIX}-drawer`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  bottomSection: `${PREFIX}-bottomSection`,
  exitButton: `${PREFIX}-exitButton`,
  list: `${PREFIX}-list`,
  link: `${PREFIX}-link`,
  activeLink: `${PREFIX}-activeLink`,
  nested: `${PREFIX}-nested`
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  [`&.${classes.drawer}`]: {
    flexShrink: 0
  },

  [`& .${classes.drawerPaper}`]: {
    width: SIDEBAR_WIDTH,
    backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : '#1A1A1A',
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundImage: theme.palette.mode === 'light' 
      ? 'linear-gradient(180deg, rgba(80, 115, 248, 0.03) 0%, rgba(255, 255, 255, 1) 100%)'
      : 'linear-gradient(180deg, rgba(80, 115, 248, 0.02) 0%, rgba(26, 26, 26, 1) 100%)'
  },

  [`& .${classes.bottomSection}`]: {
    padding: '0 16px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px'
  },

  [`& .${classes.exitButton}`]: {
    marginTop: 8,
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: 'rgba(239, 83, 80, 0.08)'
    }
  },

  [`& .${classes.list}`]: {
    padding: '8px 12px',
    flex: 1,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px'
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.mode === 'light'
        ? 'rgba(80, 115, 248, 0.3)'
        : 'rgba(80, 115, 248, 0.2)',
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.mode === 'light'
          ? 'rgba(80, 115, 248, 0.4)'
          : 'rgba(80, 115, 248, 0.3)'
      }
    }
  },

  [`& .${classes.link}`]: {
    padding: '10px 16px',
    borderRadius: 12,
    color: theme.palette.text.primary,
    cursor: 'pointer',
    userSelect: 'none',
    marginBottom: 4,
    transition: 'all 0.2s ease',
    '& + &': {
      marginTop: 0
    },
    '& svg': {
      color: theme.palette.text.secondary,
      transition: 'color 0.2s ease'
    },
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light'
        ? 'rgba(80, 115, 248, 0.08)'
        : 'rgba(80, 115, 248, 0.08)',
      '& svg': {
        color: theme.palette.primary.main
      }
    }
  },

  [`& .${classes.activeLink}`]: {
    backgroundColor: theme.palette.mode === 'light'
      ? 'rgba(80, 115, 248, 0.12)'
      : 'rgba(80, 115, 248, 0.15)',
    color: theme.palette.primary.main,
    fontWeight: 600,
    boxShadow: theme.palette.mode === 'light' 
      ? '0 0 0 1px rgba(80, 115, 248, 0.3)'
      : '0 0 0 1px rgba(80, 115, 248, 0.3)',
    '& svg': {
      color: theme.palette.primary.main
    },
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light'
        ? 'rgba(80, 115, 248, 0.18)'
        : 'rgba(80, 115, 248, 0.2)'
    }
  },

  [`& .${classes.nested}`]: {
    paddingLeft: theme.spacing(6),
    fontSize: '0.9rem'
  }
}));

export const SIDEBAR_WIDTH = 300;

interface SidebarItemProps {
  link: Route;
  nested?: boolean;
  onNavigate?: () => void;
}

const SidebarItem = (props: SidebarItemProps) => {
  const { link, onNavigate } = props;
  const checkPermission = usePermissions();

  const isHavePermission = useMemo(() => {
    if (!link?.code) {
      return true;
    }
    return checkPermission(link.code);
  }, [link?.code, checkPermission]);

  const [open, setOpen] = useState(false);

  const toggleCollapse = useCallback(
    () => setOpen(prevState => !prevState),
    []
  );

  const Item = useCallback(
    ({ item, nested, onClick }) => {
      const hasAccess = item?.code ? checkPermission(item.code) : true;

      if (!hasAccess) return null;

      const handleClick = () => {
        if (item.nested) {
          onClick?.();
        } else {
          onNavigate?.();
        }
      };

      return (
        <ListItem
          activeClassName={classes.activeLink}
          className={classNames(classes.link, nested && classes.nested)}
          component={item.nested ? 'div' : NavLink}
          key={item.path}
          to={item.path}
          onClick={handleClick}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} />
          {item.nested && (open ? <ExpandLess /> : <ExpandMore />)}
        </ListItem>
      );
    },
    [open, checkPermission, onNavigate]
  );

  if (!isHavePermission) return null;

  return (
    <>
      <Item item={link} onClick={toggleCollapse} />
      {link.nested && (
        <Collapse unmountOnExit in={open} timeout='auto'>
          <List disablePadding className={classes.list} component='div'>
            {link.nested.map((nestedLink, index) => (
              <Item key={index} nested item={nestedLink} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const Sidebar = () => {
  const dispatch = useDispatch();
  const links = useRoutes();
  const { t } = useTranslation();
  const { mode, toggleTheme } = useThemeMode();
  const { isMobile, isSidebarOpen, closeSidebar, toggleSidebar } = useResponsiveLayout();

  const cities = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);
  const auth = useSelector((state: RootState) => state.auth);
  const sidebarVisible = useSelector(
    (state: RootState) => state.app.sidebarVisible
  );

  const isHotelOnlyAdmin = useIsHotelOnlyAdmin();

  // Hotel-only admins are locked to English with no language switcher.
  useEffect(() => {
    if (isHotelOnlyAdmin) {
      changeAppLanguage('en');
    }
  }, [isHotelOnlyAdmin]);

  const [cityMenuAnchor, setCityMenuAnchor] = useState<null | HTMLElement>(null);

  const handleNavigate = useCallback(() => {
    if (isMobile) {
      closeSidebar();
    }
  }, [isMobile, closeSidebar]);

  // Filter cities based on user type
  const availableCities = useMemo(() => {
    // SuperAdmin sees all cities
    if (auth.info?.type === 'superAdmin') {
      return cities.items;
    }
    // Non-superAdmin sees only their assigned cities
    if (adminInfo.info?.cities) {
      const adminCityIds = new Set(adminInfo.info.cities.map(c => c._id));
      return cities.items.filter(city => adminCityIds.has(city._id));
    }
    return [];
  }, [auth.info?.type, adminInfo.info?.cities, cities.items]);

  const cityName = useMemo(() => {
    const city = cities.items.find(item => item._id === adminInfo.currentCity);
    if (city) {
      return city.cityName;
    }
  }, [adminInfo.currentCity, cities.items]);

  const handleCityMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCityMenuAnchor(event.currentTarget);
  };

  const handleCityMenuClose = () => {
    setCityMenuAnchor(null);
  };

  const handleCitySelect = (cityId: string) => {
    if (cityId !== adminInfo.currentCity) {
      dispatch(setCurrentCity(cityId));
      handleCityMenuClose();
      window.location.reload();
    } else {
      handleCityMenuClose();
    }
  };

  if (!sidebarVisible) {
    return null;
  }

  const drawerContent = (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 1 }}>
        <Logo alignSelf='center' />
      </Box>
      <Box sx={{ 
        textAlign: 'center', 
        pb: 2,
        pt: 1,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '& p': {
          fontSize: '0.9rem',
          fontWeight: 500,
          color: 'text.primary',
          transition: 'color 0.2s ease'
        },
        '&:hover': {
          '& p': {
            color: 'primary.main'
          }
        }
      }}
      onClick={handleCityMenuOpen}
      >
        <Typography>{cityName || '-'}</Typography>
      </Box>
      <Menu
        anchorEl={cityMenuAnchor}
        open={Boolean(cityMenuAnchor)}
        onClose={handleCityMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            maxHeight: 300,
            borderRadius: 2,
            boxShadow: (theme) => theme.palette.mode === 'light' 
              ? '0 4px 20px rgba(0, 0, 0, 0.1)'
              : '0 4px 20px rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        {availableCities.map((city) => (
          <MenuItem 
            key={city._id}
            selected={city._id === adminInfo.currentCity}
            onClick={() => handleCitySelect(city._id)}
            sx={{
              fontWeight: city._id === adminInfo.currentCity ? 600 : 400,
              color: city._id === adminInfo.currentCity 
                ? (theme) => theme.palette.mode === 'light' ? '#3D5BD4' : 'primary.main'
                : 'text.primary',
              '&.Mui-selected': {
                backgroundColor: (theme) => theme.palette.mode === 'light'
                  ? 'rgba(78, 205, 196, 0.12)'
                  : 'rgba(78, 205, 196, 0.15)',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'light'
                    ? 'rgba(78, 205, 196, 0.18)'
                    : 'rgba(78, 205, 196, 0.2)'
                }
              }
            }}
          >
            {city.cityName}
          </MenuItem>
        ))}
      </Menu>
      <List className={classes.list}>
        {links.map((link, index) => (
          <SidebarItem key={index} link={link} onNavigate={handleNavigate} />
        ))}
      </List>
      <div>
        <Divider style={{ marginBottom: 8 }} />
        <div className={classes.bottomSection}>
          <ListItem
            button
            className={`${classes.link} ${classes.exitButton}`}
            onClick={() => dispatch(signOut())}
          >
            <ListItemIcon>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary={t('common.logout')} />
          </ListItem>
          {!isHotelOnlyAdmin && <LanguageSwitcher />}
          <Tooltip title={mode === 'light' ? t('common.themeDark') : t('common.themeLight')}>
            <IconButton 
              onClick={toggleTheme} 
              sx={{ 
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'rgba(78, 205, 196, 0.08)'
                }
              }}
            >
              {mode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </>
  );

  // Detect iOS (SwipeableDrawer has issues with touch events on iOS)
  // Simplified approach: treat any device with maxTouchPoints > 1 AND iOS indicators as iOS
  const detectIOS = (): boolean => {
    if (typeof navigator === 'undefined') return false;
    
    const ua = navigator.userAgent;
    const platform = navigator.platform;
    
    // Direct iOS detection (iPhone, iPad, iPod)
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    if (platform === 'iPad') return true; // iPadOS in mobile mode
    
    // Modern iPadOS detection (13+): MacIntel + touch support
    // This includes Safari, Chrome, Firefox on iPadOS
    if (platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
    
    // Check userAgentData API (newer browsers)
    if ('userAgentData' in navigator) {
      const uaData = (navigator as any).userAgentData;
      if (uaData?.platform === 'iOS' || uaData?.platform === 'iPadOS') return true;
    }
    
    return false;
  };
  
  const isIOS = detectIOS();

  if (isMobile) {
    // Use regular Drawer on iOS to avoid SwipeArea blocking touch events
    if (isIOS) {
      return (
        <Drawer
          anchor='left'
          open={isSidebarOpen}
          onClose={closeSidebar}
          classes={{
            paper: classes.drawerPaper
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH
            }
          }}
        >
          {drawerContent}
        </Drawer>
      );
    }
    
    // Use SwipeableDrawer on other mobile devices
    return (
      <SwipeableDrawer
        anchor='left'
        open={isSidebarOpen}
        onClose={closeSidebar}
        onOpen={toggleSidebar}
        disableSwipeToOpen={true}
        classes={{
          paper: classes.drawerPaper
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH
          }
        }}
      >
        {drawerContent}
      </SwipeableDrawer>
    );
  }

  return (
    <StyledDrawer
      anchor='left'
      open={isSidebarOpen}
      classes={{
        paper: classes.drawerPaper
      }}
      className={classes.drawer}
      variant='persistent'
      sx={{
        width: isSidebarOpen ? SIDEBAR_WIDTH : 0,
        flexShrink: 0
      }}
    >
      {drawerContent}
    </StyledDrawer>
  );
};

export default Sidebar;
